/**
 * The item-link rule: the donor's `urlValidation.regex`
 * (`timmy/src/utils/common.ts:136`) with two owner-ruled changes and nothing
 * else.
 *
 * **Two named parity breaks**, both ruled on #408's decision round:
 *
 *   - the three schemes that execute are refused — #254's D11 = **c**, ruled
 *     2026-09-02; see `BLOCKED_SCHEME_PATTERN` below;
 *   - the path class carries `!$'()*,;[]{}` on top of the donor's — D1 = **b**,
 *     ruled 2026-09-09. The donor's path class omits them, so its own item-edit
 *     form (`AssetEditForm.tsx:315`) refuses a link that contains one. 66 of the
 *     8,205 migrated item links do — Google Ads tracking `!`, Aristokraft's
 *     `?prod={GUID}`, `us.farrow-ball.com/farrow's-cream/` — and every one is a
 *     working link, so under the donor's class those items could not be saved
 *     from the edit form at all, on any field, until someone rewrote the link.
 *     The owner ruled the accept set widened rather than the links broken, on
 *     the condition that it opens no XSS.
 *
 * It opens none. The added characters go in the **path** class only, never the
 * host class, so they are reachable only after a valid host and TLD followed by
 * `/` — which no `scheme:payload` value can produce, `/` being absent from the
 * host class — and the scheme guard still runs first regardless. What stays
 * outside every class is exactly what an `href` could be broken with: `<`, `>`,
 * `"`, backtick, and whitespace.
 *
 * Exported so the drift test can compare this string byte for byte against
 * Python's `LINK_URL_PATTERN` (`server/app/core/link_url.py`, the rule's home
 * since #754, which the catalog's item patch and the community link verbs both
 * import). A regex is the one contract a
 * type cannot pin, so the two sides pin each other by source — which is also why
 * the duplicated `/` the donor has in the path class is left exactly where it is
 * rather than tidied away.
 */
export const ITEM_LINK_URL_PATTERN =
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-zA-Z]{2,}(\/[-a-zA-Z0-9@:%_+.~#?&//=!$'()*,;[\]{}]*)?$/;

/**
 * The first of the two divergences: what the donor accepts and this refuses.
 *
 * The donor's host class holds `:`, `=` and `.`, so
 * `javascript:location=document.cookie`, `data:text.html` and
 * `vbscript:msgbox.doc` all *match* its regex as scheme-less hosts — and the
 * value is echoed straight into an `href`. Owner-ruled as #254's D11 = **c** on
 * 2026-09-02 and ported in #408.
 *
 * A **denylist of the schemes that execute**, not an allowlist of the two that
 * navigate. An allowlist would also throw out `mailto:someone.com`, which the
 * donor takes and which no `href` can be attacked with, so it would buy a parity
 * break for nothing.
 *
 * Exported for the same reason as the pattern above: the drift test compares its
 * source against Python's `BLOCKED_SCHEME`, because the guard is only worth
 * anything while both sides name the same three schemes.
 */
export const BLOCKED_SCHEME_PATTERN = /^(javascript|data|vbscript):/i;

/**
 * Does this read like a web address?
 *
 * **The browser's copy of Python's rule.** `ITEM_LINK_URL_PATTERN` is one
 * anchored pattern with an optional `https?://` in front, so a value with a
 * scheme and a value without take the same path — there is no parse here, and
 * that is the point. Python's `is_likely_url` (`app/core/link_url.py`) is the
 * enforcement; this exists so
 * a typist hears "Invalid URL" without a round trip, which only works while the
 * two agree, so both are the same regex plus the same guard.
 *
 * Four properties of the pattern are load-bearing, and every one is observable:
 *
 *   - the scheme group is **lowercase-only** — no `i` flag — so
 *     `HTTPS://example.com` is "Invalid URL" while `https://Example.COM` is a
 *     link, the host class holding `A-Z`;
 *   - a **TLD is mandatory**, so `http://intranet` and every other dotless host
 *     is refused, schemed or not;
 *   - the host class holds a **colon but no slash**, which makes
 *     `user:pass@example.com` an ordinary host, `ftp://example.com` a non-match,
 *     and `host:port` impossible — a port would have to be followed by another
 *     dot and a TLD, and never is;
 *   - **no class holds whitespace** — the widened path class included — so
 *     padding is refused rather than trimmed, exactly as react-hook-form's
 *     `pattern` refuses it against the value as typed. Callers used to carry
 *     their own whitespace check because the old rule handed schemed values to
 *     `new URL`, which strips padding before parsing; they no longer need one.
 *
 * An earlier Atlas rule parsed instead of matching, and a parser has no opinion
 * about ports, dotless hosts or the case of a scheme — so Atlas took
 * `https://example.com:8080/item`, `http://intranet` and `HTTPS://example.com`,
 * all three of which modsy paints "Invalid URL". #408 ported the source's rule.
 *
 * It is the rule behind both places the donor asks for a link: the item forms'
 * "Link to Item" and the rich-text editor's Link command, whose confirm button
 * it disables.
 */
export const isLikelyUrl = (value: string): boolean =>
  !BLOCKED_SCHEME_PATTERN.test(value) && ITEM_LINK_URL_PATTERN.test(value);

/** The two prefixes `withDefaultScheme` leaves alone — lowercase, as the
 *  pattern's scheme group is, so `HTTPS://x.com` is defaulted and then refused. */
const SCHEMES = ["http://", "https://"] as const;

/**
 * `https://` in front unless the value already starts with `http://` or
 * `https://` — the community link's URL (#724 Q1, a named parity divergence:
 * the donor stored `www.example.com` as typed and rendered a relative `href`).
 * Idempotent, so the browser may default and Python again. Python's
 * `with_default_scheme`, pinned by the shared vectors.
 */
export const withDefaultScheme = (value: string): string =>
  SCHEMES.some((scheme) => value.startsWith(scheme)) ? value : `https://${value}`;

/**
 * The community link rule: the scheme guard on the value **as typed**, then the
 * pattern on the defaulted value — the one that is stored. The guard reads the
 * typed value because `data:text.html` defaulted is `https://data:text.html`,
 * which the pattern alone takes. Python's `is_link_url`.
 */
export const isLinkUrl = (value: string): boolean =>
  !BLOCKED_SCHEME_PATTERN.test(value) && isLikelyUrl(withDefaultScheme(value));
